import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { authAPI } from '../services/api';
import RegisterPasswordModal from '../components/RegisterPasswordModal';
import { Wifi, Shield, Zap, Clock } from 'lucide-react';

/**
 * ========================================
 * 🔒 PROTECCIÓN DE REGISTRO HABILITADA
 * ========================================
 * Para DESHABILITAR:
 * Cambiar la siguiente línea a: false
 */
const REGISTER_PASSWORD_ENABLED = false;
// ========================================

const LoginPage = () => {
  // Updated: Dynamic field label based on user role
  const navigate = useNavigate();
  const { login } = useAuth();
  const { isDark } = useTheme();
  const [searchParams] = useSearchParams();
  const hasTimeout = searchParams.get('timeout') === 'true';
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    role: 'admin'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [maintenanceStatus, setMaintenanceStatus] = useState(null);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  // Check maintenance mode on load
  React.useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/maintenance/status-public`);
        const data = await response.json();
        setMaintenanceStatus(data);
        
        // Si el login está bloqueado, mostrar modal
        if (data.login_disabled) {
          setShowMaintenanceModal(true);
        }
      } catch (err) {
        console.error('Error checking maintenance:', err);
      }
    };
    checkMaintenance();
  }, []);


  const handleRegisterClick = () => {
    if (REGISTER_PASSWORD_ENABLED) {
      setShowPasswordModal(true);
    } else {
      navigate('/register');
    }
  };

  const handlePasswordSuccess = () => {
    setShowPasswordModal(false);
    navigate('/register');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Bloquear submit si el login está deshabilitado
    if (maintenanceStatus?.login_disabled) {
      setShowMaintenanceModal(true);
      return;
    }
    
    setLoading(true);

    try {
      const response = await authAPI.login(formData);
      login(response.data.access_token, response.data.user, response.data.license);
      
      // Redirigir según el rol
      if (response.data.user.role === 'seller') {
        navigate('/seller-sales');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('Error during login:', err);
      let errorMessage = 'Error al iniciar sesión';
      
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        // Si detail es un array de errores de validación de Pydantic
        if (Array.isArray(detail)) {
          errorMessage = detail.map(e => {
            const field = e.loc?.join('.') || 'Campo';
            return `${field}: ${e.msg}`;
          }).join('; ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center p-8 transition-colors duration-300 ${
      isDark 
        ? 'bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900' 
        : 'bg-gradient-to-br from-blue-50 via-white to-purple-50'
    }`}>
      {/* Login Form - Centered */}
      <div className="w-full max-w-md">
          <div className={`rounded-2xl shadow-xl p-8 transition-colors duration-300 ${
            isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white'
          }`}>
            <div className="text-center mb-8">
              <h2 className={`text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Iniciar Sesión</h2>
              <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>Ingresa a tu cuenta de EasyTickets</p>
            </div>

            {/* Mensaje de timeout */}
            {hasTimeout && (
              <div className={`mb-6 p-4 border rounded-lg ${
                isDark 
                  ? 'bg-yellow-900/20 border-yellow-800' 
                  : 'bg-yellow-50 border-yellow-200'
              }`}>
                <div className="flex items-start gap-3">
                  <Clock className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`} />
                  <div>
                    <p className={`text-sm font-semibold mb-1 ${isDark ? 'text-yellow-400' : 'text-yellow-800'}`}>
                      Sesión cerrada por inactividad
                    </p>
                    <p className={`text-sm ${isDark ? 'text-yellow-300' : 'text-yellow-700'}`}>
                      Tu sesión se cerró automáticamente por falta de actividad. Por favor, inicia sesión nuevamente.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className={`mb-6 p-4 border rounded-lg text-sm ${
                isDark 
                  ? 'bg-red-900/20 border-red-800 text-red-300' 
                  : 'bg-red-50 border-red-200 text-red-700'
              }`}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Tipo de Usuario
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-gray-900"
                >
                  <option value="admin">Administrador</option>
                  <option value="seller">Vendedor</option>
                  <option value="super_admin">Super Administrador</option>
                </select>
              </div>

              <div key={formData.role}>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  {formData.role === 'seller' ? 'Número de Teléfono' : 'Correo Electrónico'}
                </label>
                <input
                  type={formData.role === 'seller' ? 'tel' : 'email'}
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-gray-900"
                  placeholder={formData.role === 'seller' ? '5551234567' : 'tu@email.com'}
                  maxLength={formData.role === 'seller' ? 15 : undefined}
                  pattern={formData.role === 'seller' ? '[0-9]{7,15}' : undefined}
                />
                {formData.role === 'seller' && (
                  <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Ingresa tu número de teléfono (7-15 dígitos según tu país)</p>
                )}
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Contraseña
                </label>
                <input
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-gray-900"
                  placeholder="••••••••"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
              </button>
            </form>

            <div className="mt-8 text-center">
              <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                ¿No tienes cuenta?{' '}
                <button 
                  onClick={handleRegisterClick}
                  className={`font-semibold ${
                    isDark 
                      ? 'text-blue-400 hover:text-blue-300' 
                      : 'text-blue-600 hover:text-blue-700'
                  }`}
                >
                  Regístrate gratis
                </button>
              </p>
            </div>
          </div>
        </div>

        {/* Maintenance Mode Modal */}
        {showMaintenanceModal && maintenanceStatus && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6 relative">
              <div className="text-center">
                <div className="text-4xl mb-4">⚠️</div>
                <div className="text-gray-900 whitespace-pre-wrap text-lg leading-relaxed mb-6">
                  {maintenanceStatus.message.split('\n').map((line, i) => (
                    <React.Fragment key={i}>
                      {line}
                      {i < maintenanceStatus.message.split('\n').length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </div>
                <button
                  onClick={() => setShowMaintenanceModal(false)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de contraseña para registro */}
        <RegisterPasswordModal
          isOpen={showPasswordModal}
          onClose={() => setShowPasswordModal(false)}
          onSuccess={handlePasswordSuccess}
        />
    </div>
  );
};

export default LoginPage;
